import React, { useState, useEffect } from 'react'
import { X, Save, Loader2 } from 'lucide-react'
import api from '../api/client'
import { useLocale } from '../contexts/LocaleContext'

interface EditVolunteerSkillsModalProps {
  isOpen: boolean
  onClose: () => void
  volunteer: {
    id: string
    first_name: string
    last_name: string
    skills: string[] | null
  } | null
  onSkillsUpdated: () => void
}

const COMMON_SKILLS = [
  'School schaatsbegeleiding',
  'Kassa / schaatsverhuur',
  'Toezichthouder',
  'Passe-partouts controle',
  'Op- afbouw',
  'Technische ondersteuning',
  'BHV',
  'Social media content creatie',
  'Fotograaf',
  'Video producent'
]

const EditVolunteerSkillsModal: React.FC<EditVolunteerSkillsModalProps> = ({
  isOpen,
  onClose,
  volunteer,
  onSkillsUpdated
}) => {
  const { t } = useLocale()
  const [selectedSkills, setSelectedSkills] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    if (volunteer) {
      setSelectedSkills(volunteer.skills || [])
    }
  }, [volunteer])

  const handleSkillToggle = (skill: string) => {
    setSelectedSkills(prev =>
      prev.includes(skill)
        ? prev.filter(s => s !== skill)
        : [...prev, skill]
    )
  }

  const handleSave = async () => {
    if (!volunteer) return

    setLoading(true)
    setError('')
    setSuccess('')

    try {
      await api.profiles.update(volunteer.id, { skills: selectedSkills })
      setSuccess('Skills updated successfully')
      onSkillsUpdated()
      
      // Close modal after a short delay to show success message
      setTimeout(() => {
        onClose()
      }, 1000)
    } catch (err: any) {
      console.error('Error updating skills:', err)
      setError(err.message || 'Failed to update skills')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen || !volunteer) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {t('volunteers.editSkills')} - {volunteer.first_name} {volunteer.last_name}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-600">{success}</p>
          </div>
        )}

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            {t('volunteers.selectSkills')}
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
            {COMMON_SKILLS.map(skill => (
              <button
                key={skill}
                type="button"
                onClick={() => handleSkillToggle(skill)}
                className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                  selectedSkills.includes(skill)
                    ? 'bg-blue-100 border-blue-300 text-blue-800'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {skill}
              </button>
            ))}
          </div>
          {selectedSkills.length > 0 && (
            <p className="mt-2 text-sm text-gray-600">
              {t('volunteers.selectedSkills')}: {selectedSkills.join(', ')}
            </p>
          )}
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {loading ? t('volunteers.saving') : t('volunteers.saveSkills')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EditVolunteerSkillsModal
